// Single source of truth for "is this pricing row commercially active right
// now?". Both the pricing-source-selection decision (apiEventContentProvider's
// isCommerciallyUsablePricing) and the rendered pricing card (EventBlocks'
// PricingTeaserBlock) call this ONE predicate, so the source chosen and the
// card shown can never disagree about whether a row is on sale.
//
// DISPLAY / SOURCE-SELECTION AUTHORITY ONLY. This is not a money authority and
// must never be treated as one: it decides which price the marketing surface
// shows and which pricing source is adopted. The backend remains the sole
// authority on whether a quote can actually be created and at what price — it
// re-checks validity server-side, so the customer's browser clock can never
// unlock a real purchase.

/** Minimal structural shape of a commercially-gated pricing row. */
export interface PricingWindowRow {
  /** `false` means explicitly off sale. Absent/`true` means available subject to the window. */
  available?: boolean | null;
  /** ISO date/time. Absent means no lower bound. */
  validFrom?: string | null;
  /** ISO date/time. Absent means no upper bound. */
  validUntil?: string | null;
}

/** Parse an ISO date to epoch ms, or NaN when absent/blank/unparseable. */
function parseMs(value: string | null | undefined): number {
  if (value == null || value === "") return NaN;
  return new Date(value).getTime();
}

/**
 * Whether `row` is commercially active at the supplied instant `nowMs`.
 *
 * Semantics:
 *  - `available === false`                     → inactive
 *  - missing `validFrom`                       → no lower bound
 *  - missing `validUntil`                      → no upper bound
 *  - `validFrom <= now`                        → within lower bound (inclusive)
 *  - `validUntil >  now`                       → within upper bound (STRICT)
 *  - a PRESENT but unparseable `validFrom`/`validUntil` → fails closed (inactive)
 *
 * `nowMs` is supplied by the caller — captured ONCE per source-selection/render
 * calculation — so every row in one pass is judged against the same clock and
 * tests can freeze time deterministically.
 */
export function isPricingRowActiveAt(
  row: PricingWindowRow | null | undefined,
  nowMs: number,
): boolean {
  if (!row) return false;
  if (row.available === false) return false;

  if (row.validFrom != null && row.validFrom !== "") {
    const from = parseMs(row.validFrom);
    if (Number.isNaN(from)) return false; // present but invalid → fail closed
    if (from > nowMs) return false; // not yet on sale
  }
  if (row.validUntil != null && row.validUntil !== "") {
    const until = parseMs(row.validUntil);
    if (Number.isNaN(until)) return false; // present but invalid → fail closed
    if (until <= nowMs) return false; // expired (strict upper bound)
  }
  return true;
}
